import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Microscope, Trash2, ExternalLink } from "lucide-react";
import SynapseLoader from "@/components/SynapseLoader";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const dermaPhotoUrl = (id, w) => `${API_BASE}/derma-photos/${id}${w ? `?w=${w}` : ""}`;
const KIND_LABEL = { clinica: "Foto clinica", dermatoscopica: "Foto dermatoscopica" };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "");

// Ridimensiona lato client (max 1600 px, JPEG) per evitare upload da 5-10 MB; se il browser non decodifica (es. HEIC) invia l'originale.
async function compressImage(file, maxSide = 1600, quality = 0.86) {
  try {
    let bmp;
    try { bmp = await createImageBitmap(file, { imageOrientation: "from-image" }); } catch { bmp = await createImageBitmap(file); }
    const scale = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale); canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d").drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    return blob || file;
  } catch { return file; }
}

export const DermaPhotos = ({ patientFiscalCode, photos = [], onChange, siteSuggestions = [], markers = [] }) => {
  const cf = (patientFiscalCode || "").trim().toUpperCase();
  const cfOk = cf.length === 16;
  const [busy, setBusy] = useState(false);
  const [previous, setPrevious] = useState([]);
  const inputClin = useRef(null), inputDerm = useRef(null);

  useEffect(() => {
    if (!cfOk) { setPrevious([]); return; }
    api.get("/derma-photos", { params: { patient_fiscal_code: cf } })
      .then((r) => setPrevious(r.data.items || [])).catch(() => setPrevious([]));
  }, [cf, cfOk]);

  const currentIds = new Set(photos.map((p) => p.id));
  const candidates = previous.filter((p) => !currentIds.has(p.id));

  const upload = async (files, kind) => {
    if (!files?.length) return;
    setBusy(true);
    const added = [];
    try {
      for (const f of Array.from(files).slice(0, 8)) {
        const blob = await compressImage(f);
        const fd = new FormData();
        fd.append("file", blob, (f.name || "foto").replace(/\.[^.]+$/, "") + ".jpg");
        const { data } = await api.post(`/derma-photos/upload?patient_fiscal_code=${encodeURIComponent(cf)}&kind=${kind}`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ id: data.id, kind: data.kind, site: "", note: "", body_marker: null, created_at: data.created_at, compare_with: null });
      }
      onChange([...photos, ...added]);
      toast.success(`${added.length} foto archiviat${added.length === 1 ? "a" : "e"} nella cartella del paziente`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e, "Errore durante il caricamento della foto"));
    } finally { setBusy(false); }
  };

  const patch = (idx, changes) => {
    const next = photos.map((p, i) => (i === idx ? { ...p, ...changes } : p));
    onChange(next);
  };
  const persist = (p) => api.patch(`/derma-photos/${p.id}`, { site: p.site, note: p.note, kind: p.kind, body_marker: p.body_marker || 0 }).catch(() => {});
  const setMarker = (idx, n) => {
    const m = markers.find((k) => k.n === n);
    const p = photos[idx];
    const next = { ...p, body_marker: n || null, site: m && (!p.site || markers.some((k) => k.label === p.site)) ? m.label : p.site };
    onChange(photos.map((q, i) => (i === idx ? next : q)));
    persist(next);
  };
  const remove = async (idx) => {
    const p = photos[idx];
    try { await api.delete(`/derma-photos/${p.id}`); } catch {}
    onChange(photos.filter((_, i) => i !== idx));
  };

  return (
    <div className="rounded-lg border border-slate-300 bg-white p-3" data-testid="derma-photos-panel">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Camera className="h-4 w-4 text-blue-900" /> Documentazione fotografica</span>
        <span className="text-[11px] text-slate-500">foto salvate nella cartella del paziente, inserite nel referto e confrontabili nelle visite successive</span>
        {cfOk && (
          <a href={`/cartella-paziente/${cf}?tab=foto`} target="_blank" rel="noreferrer" data-testid="derma-photos-gallery-link"
             className="ml-auto text-[11px] text-blue-900 underline flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Galleria in cartella</a>
        )}
      </div>
      {!cfOk ? (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="derma-photos-cf-hint">
          Inserisci il codice fiscale del paziente in Anagrafica per archiviare le foto nella sua cartella.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          <input ref={inputClin} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { upload(e.target.files, "clinica"); e.target.value = ""; }} data-testid="derma-photo-input-clinica" />
          <input ref={inputDerm} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { upload(e.target.files, "dermatoscopica"); e.target.value = ""; }} data-testid="derma-photo-input-dermatoscopica" />
          <button type="button" disabled={busy} onClick={() => inputClin.current?.click()} data-testid="derma-photo-add-clinica"
                  className="h-8 px-3 rounded-md border border-blue-900 text-blue-900 bg-white hover:bg-blue-50 text-xs font-medium flex items-center gap-1.5 disabled:opacity-50">
            <Camera className="h-3.5 w-3.5" /> Foto clinica
          </button>
          <button type="button" disabled={busy} onClick={() => inputDerm.current?.click()} data-testid="derma-photo-add-dermatoscopica"
                  className="h-8 px-3 rounded-md border border-blue-900 text-blue-900 bg-white hover:bg-blue-50 text-xs font-medium flex items-center gap-1.5 disabled:opacity-50">
            <Microscope className="h-3.5 w-3.5" /> Foto dermatoscopica
          </button>
          {busy && <span className="text-xs text-slate-500 flex items-center gap-1"><SynapseLoader size="sm" inline /> Caricamento…</span>}
          {candidates.length > 0 && <span className="text-[11px] text-slate-500 self-center" data-testid="derma-photos-prev-count">{candidates.length} foto precedenti in cartella</span>}
        </div>
      )}

      {photos.length > 0 && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          {photos.map((p, i) => {
            const prev = p.compare_with ? previous.find((q) => q.id === p.compare_with) : null;
            // Opzioni di confronto: prima stessa sede, poi stesso tipo, poi le altre (max 40 più recenti)
            const norm = (s) => (s || "").trim().toLowerCase();
            const rank = (q) => (norm(q.site) && norm(q.site) === norm(p.site) ? 0 : q.kind === p.kind ? 1 : 2);
            const opts = [...candidates].sort((a, b) => rank(a) - rank(b) || (a.created_at < b.created_at ? 1 : -1)).slice(0, 40);
            return (
              <div key={p.id} className="rounded-md border border-slate-200 bg-slate-50 p-2" data-testid={`derma-photo-card-${i}`}>
                <div className="flex gap-2">
                  {prev && (
                    <div className="w-1/2">
                      <div className="text-[10px] font-semibold text-slate-500 mb-0.5">Precedente · {fmtDate(prev.created_at)}{prev.site ? ` · ${prev.site}` : ""}</div>
                      <img src={dermaPhotoUrl(prev.id, 480)} alt="Foto precedente" className="w-full h-32 object-cover rounded border border-slate-300 bg-white" data-testid={`derma-photo-prev-img-${i}`} />
                    </div>
                  )}
                  <div className={prev ? "w-1/2" : "w-full"}>
                    <div className="text-[10px] font-semibold text-blue-900 mb-0.5 flex items-center gap-1">
                      {p.body_marker ? <span className="inline-flex h-4 w-4 rounded-full bg-red-600 text-white text-[9px] font-bold items-center justify-center" data-testid={`derma-photo-marker-badge-${i}`}>{p.body_marker}</span> : null}
                      {prev ? `Oggi · ${fmtDate(p.created_at)}` : `${KIND_LABEL[p.kind] || "Foto"} · ${fmtDate(p.created_at)}`}
                    </div>
                    <a href={dermaPhotoUrl(p.id)} target="_blank" rel="noreferrer" title="Apri a schermo intero">
                      <img src={dermaPhotoUrl(p.id, 480)} alt={KIND_LABEL[p.kind] || "Foto"} className="w-full h-32 object-cover rounded border border-slate-300 bg-white" data-testid={`derma-photo-img-${i}`} />
                    </a>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-1.5">
                  {markers.length > 0 && (
                    <select value={p.body_marker || ""} onChange={(e) => setMarker(i, e.target.value ? Number(e.target.value) : null)} data-testid={`derma-photo-marker-${i}`}
                            className="h-7 rounded border border-red-300 bg-white px-1.5 text-xs col-span-2 text-slate-800">
                      <option value="">— collega a una sede della mappa corporea (opz.) —</option>
                      {markers.map((m) => <option key={m.id} value={m.n}>Sede {m.n} · {m.label}</option>)}
                    </select>
                  )}
                  <select value={p.kind} onChange={(e) => { patch(i, { kind: e.target.value }); persist({ ...p, kind: e.target.value }); }} data-testid={`derma-photo-kind-${i}`}
                          className="h-7 rounded border border-slate-300 bg-white px-1.5 text-xs">
                    <option value="clinica">Foto clinica</option><option value="dermatoscopica">Foto dermatoscopica</option>
                  </select>
                  <input list={`derma-sites-${i}`} value={p.site || ""} placeholder="Sede (es. dorso, 5 mm)" onChange={(e) => patch(i, { site: e.target.value })} onBlur={() => persist(photos[i])}
                         data-testid={`derma-photo-site-${i}`} className="h-7 rounded border border-slate-300 bg-white px-1.5 text-xs" />
                  <datalist id={`derma-sites-${i}`}>{siteSuggestions.filter(Boolean).map((s, k) => <option key={k} value={s} />)}</datalist>
                  <input value={p.note || ""} placeholder="Nota (opz.)" onChange={(e) => patch(i, { note: e.target.value })} onBlur={() => persist(photos[i])}
                         data-testid={`derma-photo-note-${i}`} className="h-7 rounded border border-slate-300 bg-white px-1.5 text-xs col-span-2" />
                  {candidates.length > 0 && (
                    <select value={p.compare_with || ""} onChange={(e) => patch(i, { compare_with: e.target.value || null })} data-testid={`derma-photo-compare-${i}`}
                            className="h-7 rounded border border-slate-300 bg-white px-1.5 text-xs col-span-2">
                      <option value="">— confronta con foto precedente (opz.) —</option>
                      {opts.map((q) => <option key={q.id} value={q.id}>{rank(q) === 0 ? "★ " : ""}{fmtDate(q.created_at)} · {KIND_LABEL[q.kind] || "Foto"}{q.body_marker ? ` · sede ${q.body_marker}` : ""}{q.site ? ` · ${q.site}` : ""}</option>)}
                    </select>
                  )}
                </div>
                <div className="mt-1.5 flex items-center justify-between">
                  <a href={dermaPhotoUrl(p.id)} target="_blank" rel="noreferrer" className="text-[11px] text-slate-500 flex items-center gap-1 hover:text-blue-900"><ExternalLink className="h-3 w-3" /> Apri</a>
                  <button type="button" onClick={() => remove(i)} className="text-[11px] text-slate-500 hover:text-red-600 flex items-center gap-1" data-testid={`derma-photo-remove-${i}`}><Trash2 className="h-3 w-3" /> Rimuovi</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
